import type { FC } from 'react';
import React from 'react';
import { AiBorderBox } from '@shared/components/AiBorderBox';
import { useTranslation } from '@shared/core';
import { addRendition } from '@shared/utils/photoRendition';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { JoyAiV2 } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import type { UploadedPreviewImages } from '@shared/components/PreviewWithPhoto/PreviewWithPhoto.types';

const STACK_OFFSET = 5;

const AI_GRADIENT = 'linear-gradient(91deg, #25AFFA 1.23%, #4951EF 46.08%, #CE54B3 83.79%)';

type CustomPhotoPreviewButtonProps = Readonly<{
  uploadedPreviewImages: UploadedPreviewImages;
  handleCustomPhotoUploadCTAClick: () => void;
  /** 'sm' renders a 48px button for dense filter bars (e.g. the card gallery); 'md' (58px) is the default. */
  size?: 'md' | 'sm';
}>;

export const CustomPhotoPreviewButton: FC<CustomPhotoPreviewButtonProps> = props => {
  const { uploadedPreviewImages, handleCustomPhotoUploadCTAClick, size = 'md' } = props;

  const { tq } = useTranslation('customPhotoPreviewButton');

  // photoSize + the AiBorderBox vertical padding set the button height (~48px for sm, ~58px for md).
  const photoSize = size === 'sm' ? 32 : 42;
  const hasPhotos = uploadedPreviewImages.length > 0;
  const visiblePhotos = uploadedPreviewImages.slice(0, 3);
  const containerWidth = hasPhotos ? photoSize + (visiblePhotos.length - 1) * STACK_OFFSET : 0;
  const buttonLabel = !hasPhotos ? tq`Preview With Your Photo` : uploadedPreviewImages.length > 1 ? tq`Change Your Photos` : tq`Change Your Photo`;

  return (
    <AiBorderBox padding={size === 'sm' ? '11px 20px 11px 11px' : '8px 20px 8px 8px'}>
      <Flex
        as="button"
        flexDirection="row"
        gap={4}
        onClick={handleCustomPhotoUploadCTAClick}
        cursor="pointer"
        alignItems="center"
        height={pxToRem(photoSize)}
      >
        {hasPhotos ? (
          <Box
            position="relative"
            width={pxToRem(containerWidth)}
            height={pxToRem(photoSize)}
            flexShrink={0}
          >
            {visiblePhotos.map((photo, index) => (
              <Box
                key={photo.id}
                position="absolute"
                top={0}
                left={pxToRem(index * STACK_OFFSET)}
                zIndex={visiblePhotos.length - index}
                height={pxToRem(photoSize)}
                width={pxToRem(photoSize)}
                borderRadius="full"
                overflow="hidden"
                border="1px solid"
                borderColor="white"
                background={`${backgroundImageForUrl(addRendition({ url: photo.url, renditionSize: 'medium' }))} lightgray`}
                backgroundRepeat="no-repeat"
                backgroundPosition="center"
                backgroundSize="cover"
              />
            ))}
          </Box>
        ) : (
          <JoyAiV2
            size={size === 'sm' ? 20 : 24}
            marginLeft={pxToRem(size === 'sm' ? 6 : 9)}
          />
        )}
        <TextV2
          typographyVariant="label6"
          fontSize={pxToRem(15)}
          background={AI_GRADIENT}
          color="transparent"
          whiteSpace="nowrap"
          css={{ backgroundClip: 'text', textShadow: 'none' }}
        >
          {buttonLabel}
        </TextV2>
      </Flex>
    </AiBorderBox>
  );
};

CustomPhotoPreviewButton.displayName = 'CustomPhotoPreviewButton';
